import { Fragment, useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  areaPath,
  donutSegments,
  intensity,
  linePath,
  nearestIndex,
  niceTicks,
  rampFill,
  scaler,
  type Plot,
} from '../chartScale';
import { compact, exact } from '../analyticsFormat';

/**
 * The chart kit. Hand-rolled SVG rather than a charting library, for three
 * reasons that all point the same way: colours come from CSS custom properties
 * so light and dark (and the embed's data-theme-on-the-host case) work without
 * threading a theme object through props; the bundle stays where it is on a
 * console an agent loads all day; and the marks match the funnel rows already
 * in CampaignResults rather than looking like a different product.
 *
 * Every chart here ships a hover layer. An SVG chart in a browser IS
 * interactive, and a chart you cannot interrogate is a picture of data.
 */


interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

/**
 * One tooltip implementation, shared by every chart below and deliberately not
 * exported: a hook beside components breaks fast refresh for the whole file,
 * and nothing outside this kit draws a chart tooltip.
 */
function useTooltip() {
  const [tip, setTip] = useState<TipState | null>(null);
  const show = useCallback((event: { clientX: number; clientY: number }, content: ReactNode) => {
    setTip({ x: event.clientX, y: event.clientY, content });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  const node = tip ? (
    <div
      className="an-tip"
      role="status"
      style={{
        // Nudged off the pointer so the tooltip never sits under the cursor,
        // and flipped near the right edge so it cannot leave the window.
        left: Math.min(tip.x + 14, window.innerWidth - 270),
        top: Math.max(8, tip.y - 12),
      }}
    >
      {tip.content}
    </div>
  ) : null;
  return { show, hide, node };
}

// ---- sparkline --------------------------------------------------------------

export function Sparkline({ values, color = 'var(--viz-1)' }: { values: number[]; color?: string }) {
  if (values.length < 2) return null;
  const plot: Plot = { width: 120, height: 28, padTop: 3, padRight: 2, padBottom: 3, padLeft: 2 };
  const scale = scaler(plot, values.length, Math.max(...values, 1));
  return (
    <svg className="an-chart" viewBox={`0 0 ${plot.width} ${plot.height}`} height={28} aria-hidden="true">
      <path d={linePath(values, scale)} className="an-series-line" stroke={color} />
    </svg>
  );
}

// ---- line / area ------------------------------------------------------------

/**
 * The drawing width in real pixels.
 *
 * An SVG with a fixed viewBox and `width: 100%` does NOT stretch — the default
 * `preserveAspectRatio` letterboxes it, so a 640-wide chart in a 940-wide card
 * sits in the middle with 150px of dead space on each side. Setting
 * `preserveAspectRatio="none"` stretches it instead, which shears the strokes
 * and the axis text. Measuring and drawing at 1:1 is the only option that does
 * neither, and it costs one ResizeObserver.
 */
function useMeasuredWidth(fallback: number) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = hostRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.round(entry.contentRect.width);
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // A tuple, not `{ ref, width }`: an object carrying a `ref` key makes every
  // value derived from it look like a ref read during render to the lint rule.
  return [hostRef, width] as const;
}

export interface LinePoint {
  label: string;
  value: number;
  previous?: number | null;
  note?: string | null;
}

export function LineChart({
  points,
  color = 'var(--viz-1)',
  height = 210,
  valueLabel = 'Sessions',
  compareLabel = 'Previous period',
  format = compact,
  extra,
}: {
  points: LinePoint[];
  color?: string;
  height?: number;
  valueLabel?: string;
  compareLabel?: string;
  format?: (value: number) => string;
  /**
   * A second series in its OWN colour — a different measure over the same
   * buckets (opens and clicks), as opposed to `previous`, which is the same
   * measure a period earlier and is drawn in the axis grey on purpose. Indexed
   * like `points`.
   */
  extra?: { label: string; color: string; values: number[] };
}) {
  const ref = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const { show, hide, node } = useTooltip();
  const [hostRef, measuredWidth] = useMeasuredWidth(640);
  const gradientId = useId();

  // After the hooks, never before: an early return above them would change the
  // hook order between an empty range and a full one.
  const width = measuredWidth;
  const plot: Plot = { width, height, padTop: 10, padRight: 10, padBottom: 22, padLeft: 38 };
  const hasPrevious = points.some((point) => point.previous !== null && point.previous !== undefined);
  const max = Math.max(
    1,
    ...points.map((point, index) => Math.max(point.value, point.previous ?? 0, extra?.values[index] ?? 0)),
  );
  const scale = scaler(plot, points.length, max);
  const ticks = niceTicks(max);
  const baseline = plot.padTop + scale.innerH;
  const values = points.map((point) => point.value);

  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((event.clientX - rect.left) / rect.width) * width;
    const index = nearestIndex(px, points.length, scale);
    setHover(index);
    const point = points[index];
    show(
      event,
      <>
        <b>{point.label}</b>
        <div className="an-tip-row">
          <span className="an-swatch" style={{ background: color }} />
          {valueLabel} <b>{exact(point.value)}</b>
        </div>
        {extra ? (
          <div className="an-tip-row">
            <span className="an-swatch" style={{ background: extra.color }} />
            {extra.label} <b>{exact(extra.values[index] ?? 0)}</b>
          </div>
        ) : null}
        {hasPrevious && point.previous !== null && point.previous !== undefined ? (
          <div className="an-tip-row">
            <span className="an-swatch" style={{ background: 'var(--viz-axis)' }} />
            {compareLabel} <b>{exact(point.previous)}</b>
          </div>
        ) : null}
        {point.note ? <div style={{ opacity: 0.8, marginTop: 3 }}>{point.note}</div> : null}
      </>,
    );
  };

  // Roughly eight labels, whatever the range length.
  const labelEvery = Math.max(1, Math.ceil(points.length / 8));

  return (
    <div ref={hostRef}>
      {!points.length ? (
        <div className="an-empty">Nothing in this range yet.</div>
      ) : (
        <>
      <svg
        ref={ref}
        className="an-chart"
        viewBox={`0 0 ${width} ${height}`}
        style={{ height }}
        role="img"
        aria-label={`${valueLabel} over time`}
        onPointerMove={onMove}
        onPointerLeave={() => {
          setHover(null);
          hide();
        }}
      >
        <title>{`${valueLabel} over time`}</title>
        {ticks.map((tick) => (
          <g key={tick}>
            <line className="an-grid-line" x1={plot.padLeft} x2={width - plot.padRight} y1={scale.y(tick)} y2={scale.y(tick)} />
            <text x={plot.padLeft - 7} y={scale.y(tick) + 3.5} textAnchor="end">
              {format(tick)}
            </text>
          </g>
        ))}
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.20" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
        </defs>
        <path d={areaPath(values, scale, baseline)} fill={`url(#${gradientId})`} />
        {hasPrevious ? (
          <path
            d={linePath(
              points.map((point) => point.previous ?? 0),
              scale,
            )}
            className="an-series-prev"
          />
        ) : null}
        <path d={linePath(values, scale)} className="an-series-line" stroke={color} />
        {extra ? (
          <path
            d={linePath(
              points.map((_, index) => extra.values[index] ?? 0),
              scale,
            )}
            className="an-series-line"
            stroke={extra.color}
          />
        ) : null}
        {points.map((point, index) =>
          index % labelEvery === 0 || index === points.length - 1 ? (
            <text key={point.label} x={scale.x(index)} y={height - 6} textAnchor="middle">
              {point.label}
            </text>
          ) : null,
        )}
        {/* An annotation is a fact about the DAY, not about the value, so it is
            marked on the axis rather than on the line — a dot on the series
            would read as a data point of a different kind. */}
        {points.map((point, index) =>
          point.note ? (
            <g key={`note-${point.label}`}>
              <line
                className="an-note-rule"
                x1={scale.x(index)}
                x2={scale.x(index)}
                y1={plot.padTop}
                y2={baseline}
              />
              <path
                className="an-note-flag"
                d={`M${scale.x(index) - 4} ${baseline + 5}L${scale.x(index) + 4} ${baseline + 5}L${scale.x(index)} ${baseline}Z`}
              />
            </g>
          ) : null,
        )}
        {hover !== null ? (
          <g>
            <line className="an-crosshair" x1={scale.x(hover)} x2={scale.x(hover)} y1={plot.padTop} y2={baseline} />
            <circle className="an-marker" cx={scale.x(hover)} cy={scale.y(points[hover].value)} r={4.5} fill={color} />
            {extra ? (
              <circle
                className="an-marker"
                cx={scale.x(hover)}
                cy={scale.y(extra.values[hover] ?? 0)}
                r={4.5}
                fill={extra.color}
              />
            ) : null}
          </g>
        ) : null}
      </svg>
      {hasPrevious || extra ? (
        <div className="an-legend">
          <span>
            <i className="an-swatch" style={{ background: color }} /> {valueLabel}
          </span>
          {extra ? (
            <span>
              <i className="an-swatch" style={{ background: extra.color }} /> {extra.label}
            </span>
          ) : null}
          {hasPrevious ? (
            <span>
              <i className="an-swatch" style={{ background: 'var(--viz-axis)' }} /> {compareLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      {node}
        </>
      )}
    </div>
  );
}

// ---- bar list ---------------------------------------------------------------

export interface BarRow {
  label: string;
  value: number;
  share: number;
  meta?: string;
  color?: string;
  onSelect?: () => void;
}

/**
 * The workhorse: top pages, sources, geo, devices, campaigns and the funnel are
 * all this. Values are ALWAYS shown beside the bar — that is the relief the
 * contrast warning on the amber slot obliges, and it means a reader never has
 * to judge a length against a scale.
 */
export function BarList({
  rows,
  emptyLabel = 'Nothing here yet.',
  swatches = false,
}: {
  rows: BarRow[];
  emptyLabel?: string;
  /**
   * Draw each row's colour as a dot before its label.
   *
   * Only for a list whose rows are genuinely DIFFERENT things and are coloured
   * accordingly — the channel breakdown beside its donut, where the dot is what
   * ties a row to its slice. A ranked list of one measure stays undotted: there
   * the colour is a single hue and a dot per row would imply a distinction the
   * data does not carry.
   */
  swatches?: boolean;
}) {
  const { show, hide, node } = useTooltip();
  if (!rows.length) return <div className="an-empty">{emptyLabel}</div>;
  // One decision for the whole list rather than per row: a meta column that
  // appears on some rows and not others leaves the values in a ragged line.
  const hasMeta = rows.some((row) => Boolean(row.meta));
  return (
    <>
      <div className={`an-bars${hasMeta ? ' with-meta' : ''}`}>
        {rows.map((row) => (
          <div
            key={row.label}
            className={`an-bar-row${row.onSelect ? ' clickable' : ''}`}
            onClick={row.onSelect}
            onKeyDown={(event) => {
              if (row.onSelect && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                row.onSelect();
              }
            }}
            role={row.onSelect ? 'button' : undefined}
            tabIndex={row.onSelect ? 0 : undefined}
            onPointerEnter={(event) =>
              show(
                event,
                <>
                  <b>{row.label}</b>
                  <div>{exact(row.value)}</div>
                  {row.meta ? <div style={{ opacity: 0.8 }}>{row.meta}</div> : null}
                </>,
              )
            }
            onPointerLeave={hide}
          >
            <span className="an-bar-label" title={row.label}>
              {swatches ? <i className="an-swatch" style={{ background: row.color ?? 'var(--viz-1)' }} /> : null}
              <span className="an-bar-text">{row.label}</span>
            </span>
            {hasMeta ? <span className="an-bar-meta">{row.meta ?? ''}</span> : null}
            <span className="an-bar-value">{compact(row.value)}</span>
            <span className="an-bar-share">{(row.share * 100).toFixed(0)}%</span>
            <span className="an-bar-track">
              <span
                className="an-bar-fill"
                style={{
                  width: `${Math.max(1, row.share * 100)}%`,
                  background: row.color ?? 'var(--viz-1)',
                }}
              />
            </span>
          </div>
        ))}
      </div>
      {node}
    </>
  );
}

// ---- donut ------------------------------------------------------------------

export function Donut({
  slices,
  size = 132,
  legend = true,
}: {
  slices: { label: string; value: number; color: string }[];
  /** The ring's box. The stroke and the centre figure scale with it. */
  size?: number;
  /**
   * False when the caller draws its own key.
   *
   * The identity-never-rests-on-colour rule is not relaxed by this — it is
   * discharged elsewhere: the acquisition card's ranked list beside the ring
   * carries the same swatch, the label, the value and the share, which is more
   * than this legend said. Pass false ONLY when such a list is present.
   */
  legend?: boolean;
}) {
  const { show, hide, node } = useTooltip();
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  if (!total) return <div className="an-empty">Nothing in this range yet.</div>;
  const centre = size / 2;
  const radius = size * 0.394;
  const stroke = size * 0.121;
  const circumference = 2 * Math.PI * radius;
  const segments = donutSegments(
    slices.map((slice) => slice.value),
    circumference,
  );
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Share by channel">
          <title>Share by channel</title>
          {segments.map((segment, index) => (
            <circle
              key={slices[index].label}
              cx={centre}
              cy={centre}
              r={radius}
              fill="none"
              stroke={slices[index].color}
              strokeWidth={stroke}
              strokeDasharray={`${segment.length} ${circumference - segment.length}`}
              strokeDashoffset={segment.offset}
              transform={`rotate(-90 ${centre} ${centre})`}
              onPointerEnter={(event) =>
                show(
                  event,
                  <>
                    <b>{slices[index].label}</b>
                    <div>
                      {exact(slices[index].value)} · {(segment.share * 100).toFixed(1)}%
                    </div>
                  </>,
                )
              }
              onPointerLeave={hide}
            />
          ))}
          <text
            x={centre}
            y={centre - 2}
            textAnchor="middle"
            style={{ fontSize: size * 0.144, fontWeight: 640, fill: 'var(--c-fg)' }}
          >
            {compact(total)}
          </text>
          {/* The fill is named on purpose: this svg is not an `.an-chart`, so an
              unstyled <text> painted SVG's default black — invisible in dark. */}
          <text x={centre} y={centre + 13} textAnchor="middle" style={{ fontSize: 10, fill: 'var(--c-muted)' }}>
            sessions
          </text>
        </svg>
        {/* Present for two or more series unless the caller draws its own key,
            so identity is never carried by colour alone. */}
        <div
          className="an-legend"
          style={{ flexDirection: 'column', gap: 5, marginTop: 0, display: legend ? undefined : 'none' }}
        >
          {slices.map((slice, index) => (
            <span key={slice.label}>
              <i className="an-swatch" style={{ background: slice.color }} />
              {slice.label}
              <b style={{ color: 'var(--c-fg)', marginLeft: 2 }}>{compact(slice.value)}</b>
              <span style={{ opacity: 0.75 }}>{(segments[index].share * 100).toFixed(0)}%</span>
            </span>
          ))}
        </div>
      </div>
      {node}
    </>
  );
}

// ---- the 7x24 matrix --------------------------------------------------------

export function Matrix({
  cells,
  max,
  dowLabels,
}: {
  cells: { dow: number; hour: number; sessions: number }[];
  max: number;
  dowLabels: string[];
}) {
  const { show, hide, node } = useTooltip();
  const byKey = new Map(cells.map((cell) => [`${cell.dow}:${cell.hour}`, cell.sessions]));
  const hours = Array.from({ length: 24 }, (_, hour) => hour);
  return (
    <>
      <div className="an-matrix">
        <span />
        {hours.map((hour) => (
          <span key={hour} className="an-matrix-hour">
            {hour % 6 === 0 ? hour : ''}
          </span>
        ))}
        {dowLabels.map((label, dow) => (
          // A keyed Fragment, not `<>`: the row spreads 25 children straight
          // into the grid, so there is no wrapper element to carry the key.
          <Fragment key={dow}>
            <span className="an-matrix-dow">
              {label}
            </span>
            {hours.map((hour) => {
              const sessions = byKey.get(`${dow}:${hour}`) ?? 0;
              const weight = intensity(sessions, max);
              return (
                <span
                  key={`${dow}:${hour}`}
                  className="an-matrix-cell"
                  // A SEQUENTIAL ramp: one hue, quiet to busy. Never a rainbow.
                  style={{ background: rampFill(weight) }}
                  onPointerEnter={(event) =>
                    show(
                      event,
                      <>
                        <b>
                          {label} {String(hour).padStart(2, '0')}:00
                        </b>
                        <div>{exact(sessions)} sessions</div>
                      </>,
                    )
                  }
                  onPointerLeave={hide}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
      {node}
    </>
  );
}
